import { Injectable, Injector } from '@angular/core';
import { Observable, of } from 'rxjs';
import { KeyValue, LoaderSpinnerTypes } from '@carm/_core';
import { BaseApiService } from '@carm/_core/baseapi.service';
import { catchError, map, mergeMap, shareReplay } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import {
  AdvancedSearchCheckboxes,
  AdvancedSearchConfigResponse,
  AdvancedSearchOption,
  SearchOptionEnum,
} from '@carm/shared/components/advanced-search/models/advanced-search.model';
import { IKeyValue } from '@carm/_utils/models/key-value.model';
import * as dayjs from 'dayjs';
import { dateFormat } from '@carm/shared/constants/const-carm';
import {
  CadMetadataResponse,
  IClassificationCodeDetailResponse,
  IGetReasonCodesResponse,
} from '../models/cad-metadata.model';
import { ProgramAccountProfileService } from '@carm/_core/program-account-profile/services/program-account-profile.service';
import { ISubProgramActiveResponse } from '@carm/_core/program-account-profile/models/program-account-profile-api.model';
import { SubProgramEnrolmentType } from '@carm/_core/program-enrolment/models/enrol-program-enums.model';
import { environment } from 'src/environments/environment';
import { DeclarationType } from '../models/common';

@Injectable()
export class CadMetadataService extends BaseApiService {
  private modesOfTransportCache$: Observable<KeyValue[]>;
  private offRegionsCache$: Observable<KeyValue[]>;
  private officeSubCodesCashe$: Observable<KeyValue[]>;
  private currenciesCache$: Observable<KeyValue[]>;
  private dutyCodesCache$: Observable<KeyValue[]>;
  private canadianProvincesCache$: Observable<KeyValue[]>;
  private usPortsCache$: Observable<KeyValue[]>;
  private subTypesCache$: Observable<KeyValue[]>;
  private simaCodesCache$: Observable<KeyValue[]>;
  private simaSubjectCodesCache$: Observable<KeyValue[]>;
  private surtaxAndSafeguardSubjectCodesCache$: Observable<KeyValue[]>;
  private appealProgramCodesCache$: Observable<KeyValue[]>;
  private declarationReasonsCache$: Observable<KeyValue[]>;
  private timeLimitTypesCache$: Observable<KeyValue[]>;
  private portOfLadingRegionCache$: Observable<KeyValue[]>;
  private invoiceTypesCache$: Observable<KeyValue[]>;
  private incoTermsCache$: Observable<KeyValue[]>;
  private warehouseCache$: Observable<KeyValue[]>;
  private gstCodesCache$: Observable<KeyValue[]>;
  private countriesCache$: Observable<KeyValue[]>;
  private tariffCodesCache$: Observable<KeyValue[]>;
  private declarationTypesCache$: Observable<KeyValue[]>;
  private allOfficeCodesCache$: Observable<KeyValue[]>;

  get cadMetadataUrl(): string {
    return `${this.endPointBP}/cad/metadata`;
  }

  constructor(protected injector: Injector,
    protected programAccountProfileService: ProgramAccountProfileService) {
    super(injector);
  }

  get modesOfTransport(): Observable<KeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/transportMode`;
    this.modesOfTransportCache$ =
      this.modesOfTransportCache$ ||
      this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
        map(keyValueResponse => keyValueResponse?.keyValue ?? []),
        shareReplay(1)
      );

    return this.modesOfTransportCache$;
  }

  get warehouses(): Observable<KeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/warehouseCodes`;
    this.warehouseCache$ =
      this.warehouseCache$ ||
      this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
        map(keyValueResponse => keyValueResponse?.keyValue ?? []),
        shareReplay(1)
      );

    return this.warehouseCache$;
  }

  getReportTypes(): Observable<any> {
    return this.httpAuthGet<any>(`${environment.baseApiUrls}/metadata/key/cad_report_type`);
  }

  getWarehousesByReleaseDate(releaseDate: string): Observable<KeyValue[]> {
    let params: HttpParams = null;

    if (releaseDate) {
      params = new HttpParams().set('date', releaseDate);
    }

    const endPoint = `${this.cadMetadataUrl}/warehouseCodes`;
    return this.httpAuthGet<CadMetadataResponse>(endPoint, params).pipe(
      map(keyValueResponse => keyValueResponse?.keyValue ?? [])
    );
  }

  get types(): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/type`;
    this.declarationTypesCache$ = this.declarationTypesCache$ || this.programAccountProfileService.getActiveSubPrograms(LoaderSpinnerTypes.FullPage).pipe(
      catchError(err => { // if an access denied, don't filter (fallback)
        return of(null);
      }),
      mergeMap((subPrograms: ISubProgramActiveResponse) => {
        return this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
          map(keyValueResponse => subPrograms ? this.filterTTType(keyValueResponse?.keyValue, subPrograms) : keyValueResponse?.keyValue)
        );
      }),
      shareReplay(1)
    )

    return this.declarationTypesCache$;
  }

  getSubTypesByTypeId(type: string): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/subtype?id=${type}`;
    return this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
      map(keyValueResponse => keyValueResponse?.keyValue ?? [])
    );
  }

  get officeRegions(): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/officeRegion`;
    this.offRegionsCache$ =
      this.offRegionsCache$ ||
      this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
        map(keyValueResponse => keyValueResponse?.keyValue ?? []),
        shareReplay(1)
      );

    return this.offRegionsCache$;
  }

  getOfficeRegionByOfficeId(officeId: string): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/officeRegion?id=${officeId}`;
    return this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
      map(keyValueResponse => keyValueResponse?.keyValue ?? [])
    );
  }

  get allOfficeCodes(): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/office?id=`;
    this.allOfficeCodesCache$ = this.allOfficeCodesCache$ || this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
      map(keyValueResponse => keyValueResponse?.keyValue ?? []),
      shareReplay(1)
    );

    return this.allOfficeCodesCache$;
  }

  getOfficeCodesByRegionId(region: string): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/office?id=${region}`;
    return this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
      map(keyValueResponse => keyValueResponse?.keyValue ?? [])
    );
  }

  get subOfficeCodes(): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/subOfficeCodes`;
    this.officeSubCodesCashe$ =
      this.officeSubCodesCashe$ ||
      this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
        map(keyValueResponse => keyValueResponse?.keyValue ?? []),
        shareReplay(1)
      );

    return this.officeSubCodesCashe$;
  }

  getSafeguardCodes(
    releaseDate: string
  ): Observable<IKeyValue[]> {
    let params = new HttpParams();

    if (releaseDate) {
      params = params.append('date', releaseDate);
    }

    const endPoint = `${this.cadMetadataUrl}/safeguardCodes`;
    return this.httpAuthGet<CadMetadataResponse>(endPoint, params).pipe(
      map(keyValueResponse => keyValueResponse?.keyValue ?? [])
    );
  }

  getSurtaxCodes(
    releaseDate: string
  ): Observable<IKeyValue[]> {
    let params = new HttpParams();
    
    if (releaseDate) {
      params = params.append('date', releaseDate);
    }

    const endPoint = `${this.cadMetadataUrl}/surtax`;
    return this.httpAuthGet<CadMetadataResponse>(endPoint, params).pipe(
      map(keyValueResponse => keyValueResponse?.keyValue ?? [])
    );
  }

  getSurtaxBySafeguardId(safeguardId: string): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/surtax?id=${safeguardId}`;
    return this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
      map(keyValueResponse => keyValueResponse?.keyValue ?? [])
    );
  }

  getUnitOfMeasureCodes(dimension?: string): Observable<IKeyValue[]> {
    const params = new HttpParams().set('id', dimension);

    const endPoint = `${this.cadMetadataUrl}/uomCodes`;
    return this.httpAuthGet<CadMetadataResponse>(endPoint, dimension ? params : null).pipe(
      map(keyValueResponse => keyValueResponse?.keyValue ?? [])
    );
  }

  get gstCodes(): Observable<KeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/gstCodes`;
    this.gstCodesCache$ =
      this.gstCodesCache$ ||
      this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
        map(keyValueResponse => keyValueResponse?.keyValue ?? []),
        shareReplay(1)
      );

    return this.gstCodesCache$;
  }

  getGstCodesByReleaseDate(releaseDate: string): Observable<KeyValue[]> {
    let qParams: HttpParams;

    if (releaseDate) {
      qParams = new HttpParams().set('date', releaseDate);
    } else {
      qParams = new HttpParams();
    }

    const endPoint = `${this.cadMetadataUrl}/gstCodes`;
    return this.httpAuthGet<CadMetadataResponse>(endPoint, qParams).pipe(
      map(keyValueResponse => keyValueResponse?.keyValue ?? [])
    );
  }

  get tariffCodes(): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/tariffCode`;
    this.tariffCodesCache$ =
      this.tariffCodesCache$ ||
      this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
        map(keyValueResponse => keyValueResponse?.keyValue ?? []),
        shareReplay(1)
      );

    return this.tariffCodesCache$;
  }

  getTariffCodesByReleaseDate(releaseDate: string): Observable<IKeyValue[]> {
    let qParams: HttpParams;

    if (releaseDate) {
      qParams = new HttpParams().set('date', releaseDate);
    } else {
      qParams = new HttpParams();
    }

    const endPoint = `${this.cadMetadataUrl}/tariffCode`;
    return this.httpAuthGet<CadMetadataResponse>(endPoint, qParams).pipe(
      map(keyValueResponse => keyValueResponse?.keyValue ?? [])
    );
  }

  getTariffTreatments(
    countryOrigin: string,
    classificationCode: string,
    placeExport: string,
    releaseDate: string
  ): Observable<IKeyValue[]> {
    let params = new HttpParams()
      .set('countryOrigin', countryOrigin)
      .set('id', classificationCode)
      .set('placeExport', placeExport);

    if (releaseDate) {
      params = params.append('date', releaseDate);
    }

    const endPoint = `${this.cadMetadataUrl}/tariffTreatment`;
    return this.httpAuthGet<CadMetadataResponse>(endPoint, params).pipe(
      map(keyValueResponse => keyValueResponse?.keyValue ?? [])
    );
  }

  get currencies(): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/currCodes`;
    this.currenciesCache$ =
      this.currenciesCache$ ||
      this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
        map(keyValueResponse => keyValueResponse?.keyValue ?? []),
        shareReplay(1)
      );

    return this.currenciesCache$;
  }

  get dutyCodes(): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/vfd`;
    this.dutyCodesCache$ =
      this.dutyCodesCache$ ||
      this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
        map(keyValueResponse => keyValueResponse?.keyValue ?? []),
        shareReplay(1)
      );

    return this.dutyCodesCache$;
  }

  getExciseCodes(
    classificationCode: string,
    releaseDate: string
  ): Observable<IKeyValue[]> {
    let qParams: HttpParams;

    if (releaseDate) {
      qParams = new HttpParams()
        .set('date', releaseDate)
        .set('id', classificationCode);
    } else {
      qParams = new HttpParams().set('id', classificationCode);
    }

    const endPoint = `${this.cadMetadataUrl}/exciseTaxCode`;
    return this.httpAuthGet<CadMetadataResponse>(endPoint, qParams).pipe(
      map(keyValueResponse => keyValueResponse?.keyValue ?? [])
    );
  }

  get countries(): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/countryCode`;
    this.countriesCache$ =
      this.countriesCache$ ||
      this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
        map(keyValueResponse => keyValueResponse?.keyValue ?? []),
        shareReplay(1)
      );

    return this.countriesCache$;
  }

  getCountriesByReleaseDate(
    releaseDate: string = null
  ): Observable<IKeyValue[]> {
    let qParams: HttpParams;

    if (releaseDate) {
      qParams = new HttpParams().set('date', releaseDate);
    } else {
      qParams = new HttpParams();
    }

    const endPoint = `${this.cadMetadataUrl}/countryCode`;
    return this.httpAuthGet<CadMetadataResponse>(endPoint, qParams).pipe(
      map(keyValueResponse => keyValueResponse?.keyValue ?? []),
      map(countries => {
        const canadaAndUsCodes = ['CA', 'US'];
        const onlyCanadaAndUs = countries?.filter(country => canadaAndUsCodes.includes(country.key)).slice().sortByValue();
        const allExceptCanadaAndUs = countries?.slice().sortByValue().filter(country => !canadaAndUsCodes.includes(country.key));
        return [
          ...onlyCanadaAndUs,
          ...allExceptCanadaAndUs
        ];
      })
    );
  }

  getRegionsByCountryId(countryId: string): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/regionCode?id=${countryId}`;
    return this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
      map(keyValueResponse => keyValueResponse?.keyValue ?? [])
    );
  }

  get canadianProvinces(): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/caProvinces`;
    this.canadianProvincesCache$ =
      this.canadianProvincesCache$ ||
      this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
        map(keyValueResponse => keyValueResponse?.keyValue ?? []),
        shareReplay(1)
      );

    return this.canadianProvincesCache$;
  }

  get usPorts(): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/usPoe`;
    this.usPortsCache$ =
      this.usPortsCache$ ||
      this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
        map(keyValueResponse => keyValueResponse?.keyValue ?? []),
        shareReplay(1)
      );

    return this.usPortsCache$;
  }

  get subTypes(): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/subtype`;
    this.subTypesCache$ =
      this.subTypesCache$ ||
      this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
        map(keyValueResponse => keyValueResponse?.keyValue ?? []),
        shareReplay(1)
      );

    return this.subTypesCache$;
  }

  get simaCodes(): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/simaCodes`;
    this.simaCodesCache$ =
      this.simaCodesCache$ ||
      this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
        map(keyValueResponse => keyValueResponse?.keyValue ?? []),
        shareReplay(1)
      );

    return this.simaCodesCache$;
  }

  get simaSubjectCodes(): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/simaSubjectCodes`;
    this.simaSubjectCodesCache$ =
      this.simaSubjectCodesCache$ ||
      this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
        map(keyValueResponse => keyValueResponse?.keyValue ?? []),
        shareReplay(1)
      );

    return this.simaSubjectCodesCache$;
  }

  getSimaMeasureInForces(
    countryOrigin: string,
    classificationCode: string,
    placeExport: string,
    releaseDate: string
  ): Observable<IKeyValue[]> {
    let params = new HttpParams()
      .set('countryOrigin', countryOrigin)
      .set('id', classificationCode)
      .set('placeExport', placeExport);

    if (releaseDate) {
      params = params.append('date', releaseDate);
    }

    const endPoint = `${this.cadMetadataUrl}/measureForce`;
    return this.httpAuthGet<CadMetadataResponse>(endPoint, params).pipe(
      map(keyValueResponse => keyValueResponse?.keyValue ?? [])
    );
  }

  get surtaxAndSafeguardSubjectCodes(): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/sursafSubjectCodes`;
    this.surtaxAndSafeguardSubjectCodesCache$ =
      this.surtaxAndSafeguardSubjectCodesCache$ ||
      this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
        map(keyValueResponse => keyValueResponse?.keyValue ?? []),
        shareReplay(1)
      );

    return this.surtaxAndSafeguardSubjectCodesCache$;
  }

  get appealProgramCodes(): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/appealProgramCode`;
    this.appealProgramCodesCache$ =
      this.appealProgramCodesCache$ ||
      this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
        map(keyValueResponse => keyValueResponse?.keyValue ?? []),
        shareReplay(1)
      );

    return this.appealProgramCodesCache$;
  }

  get declarationReasons(): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/declarationReasonCodes`;
    this.declarationReasonsCache$ =
      this.declarationReasonsCache$ ||
      this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
        map(keyValueResponse => keyValueResponse?.keyValue ?? []),
        shareReplay(1)
      );

    return this.declarationReasonsCache$;
  }

  get timeLimitTypes(): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/timeLimitType`;
    this.timeLimitTypesCache$ =
      this.timeLimitTypesCache$ ||
      this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
        map(keyValueResponse => keyValueResponse?.keyValue ?? []),
        shareReplay(1)
      );

    return this.timeLimitTypesCache$;
  }

  getPortOfLading(region: string): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/unladingPortsRegions?id=${region}`;
    return this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
      map(keyValueResponse => keyValueResponse?.keyValue ?? [])
    );
  }

  get portOfLadingRegion(): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/unladingPortsRegions`;
    this.portOfLadingRegionCache$ =
      this.portOfLadingRegionCache$ ||
      this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
        map(keyValueResponse => keyValueResponse?.keyValue ?? []),
        shareReplay(1)
      );

    return this.portOfLadingRegionCache$;
  }

  getSearchOptionsFor(
    option: SearchOptionEnum
  ): Observable<AdvancedSearchConfigResponse> {
    const endPoint = `${this.cadMetadataUrl}/search/${option}`;
    return this.httpAuthGet<AdvancedSearchConfigResponse>(endPoint).pipe(
      map((advanceSearch: AdvancedSearchConfigResponse) => {
        return {
          searchOptions: advanceSearch.searchOptions?.map(
            (advanceSearchOption: AdvancedSearchOption) => {
              advanceSearchOption.key = advanceSearchOption.value; // api return number for key. use identifier
              advanceSearchOption.value = this.translateService.instant(
                `concepts.declarations.advanceSearchOptions.${advanceSearchOption.value}`
              );
              return advanceSearchOption;
            }
          ),
          checkboxes: advanceSearch.checkboxes?.map(
            (checkboxes: AdvancedSearchCheckboxes) => {
              checkboxes.value = this.translateService.instant(
                `concepts.declarations.advanceSearchOptions.${checkboxes.value}`
              );
              return checkboxes;
            }
          ),
        } as AdvancedSearchConfigResponse;
      })
    );
  }

  get invoiceTypes(): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/invoiceType`;
    this.invoiceTypesCache$ =
      this.invoiceTypesCache$ ||
      this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
        map(keyValueResponse => keyValueResponse?.keyValue ?? []),
        shareReplay(1)
      );

    return this.invoiceTypesCache$;
  }

  get incoTerms(): Observable<IKeyValue[]> {
    const endPoint = `${this.cadMetadataUrl}/incoterms`;
    this.incoTermsCache$ =
      this.incoTermsCache$ ||
      this.httpAuthGet<CadMetadataResponse>(endPoint).pipe(
        map(keyValueResponse => keyValueResponse?.keyValue ?? []),
        shareReplay(1)
      );

    return this.incoTermsCache$;
  }

  getReasons(): Observable<IGetReasonCodesResponse> {
    const endPoint = `${this.cadMetadataUrl}/reasons`;
    return this.httpAuthGet<IGetReasonCodesResponse>(endPoint);
  }

  getlevelOfAdjustments(): Observable<IKeyValue[]> {
    const levels: IKeyValue[] = [
      {
        key: 'H',
        value: this.translateService.instant(
          'concepts.declarations.roi.tabs.general_details'
        ),
      },
      {
        key: 'S',
        value: this.translateService.instant(
          'concepts.declarations.roi.tabs.invoice'
        ),
      },
      {
        key: 'C',
        value: this.translateService.instant(
          'concepts.declarations.roi.tabs.commodities'
        ),
      },
    ];

    return of(levels);
  }

  getWithdrawProgramCodes(): Observable<IKeyValue[]> {
    return of([
      { key: '1', value: 'Classification' },
      { key: '2', value: 'Origination' },
      { key: '3', value: 'Valuation' },
      { key: '4', value: 'N/A' },
    ]);
  }

  get precarmTypes(): Observable<IKeyValue[]> {

    const precarmTypes: IKeyValue[] = [
      { key: "1", value: this.translateService.instant('concepts.declarations.create_declaration.precarmTypes.isDeclared') },
      // { key: "2", value: this.translateService.instant('concepts.declarations.create_declaration.precarmTypes.isAdjusted') },
    ]

    return of(precarmTypes);
  }

  get precarmTTTypes(): Observable<IKeyValue[]> {

    const precarmTypes: IKeyValue[] = [
      // { key: "1", value: this.translateService.instant('concepts.declarations.create_declaration.precarmTypes.isDeclared') },
      { key: "2", value: this.translateService.instant('concepts.declarations.create_declaration.precarmTypes.isAdjusted') },
    ]

    return of(precarmTypes);
  }


  getClassificationCodeDetail(
    classificationCode: string,
    releaseDate?: string
  ): Observable<IClassificationCodeDetailResponse> {
    const qParams = new HttpParams().set(
      'date',
      releaseDate ? releaseDate : dayjs().format(dateFormat)
    );

    const endPoint = `${this.cadMetadataUrl}/classification-code/${classificationCode}`;
    return this.httpAuthGet<IClassificationCodeDetailResponse>(
      endPoint,
      qParams
    );
  }


  private filterTTType(keyValues: KeyValue[], subPrograms: ISubProgramActiveResponse){
   if (subPrograms && subPrograms.enrolledSubProgram.includes(SubProgramEnrolmentType.TrustedTrader)){
      return keyValues;
    } else {
      return keyValues.filter(kv=> kv.key !== DeclarationType.CustomsSelfAssessment_TT);
    }
  }

}
